// 서버 점검(재시작) 중 표시 화면
// - MaintenanceScreen: 세션 확인/로그인 단계에서 서버가 응답하지 않을 때 앱 전체를 대체(A안)
// - MaintenanceDialog: 앱 사용 중 서버가 응답하지 않을 때 현재 화면 위에 덮는 모달(C안)
// 둘 다 /api/health 를 주기적으로 확인하다가 복구되면 스스로 닫힌다.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { piano } from '../theme/pianoTheme';
import { checkServerHealth, markServerUp } from '../api/serverStatus';

const RETRY_SECONDS = 5;

function useIsOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

// RETRY_SECONDS 간격으로 헬스체크하고, 성공하면 onRecovered 를 한 번 호출한다.
// 탭 복귀·네트워크 재연결 시에는 카운트다운을 기다리지 않고 바로 확인한다.
function useRecoveryPolling(onRecovered) {
  const [secondsLeft, setSecondsLeft] = useState(RETRY_SECONDS);
  const [checking, setChecking] = useState(false);
  const checkingRef = useRef(false);
  const recoveredRef = useRef(false);
  const onRecoveredRef = useRef(onRecovered);

  useEffect(() => {
    onRecoveredRef.current = onRecovered;
  }, [onRecovered]);

  const checkNow = useCallback(async () => {
    if (checkingRef.current || recoveredRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    const ok = await checkServerHealth();
    checkingRef.current = false;
    setChecking(false);
    setSecondsLeft(RETRY_SECONDS);
    if (ok) {
      recoveredRef.current = true;
      onRecoveredRef.current();
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (checkingRef.current) return;
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) checkNow();
  }, [secondsLeft, checkNow]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', checkNow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', checkNow);
    };
  }, [checkNow]);

  return { secondsLeft, checking, checkNow };
}

function RetryStatus({ checking, secondsLeft, gold }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        color: 'text.secondary',
        fontSize: 13,
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: gold }} />
      {checking ? '연결 확인 중...' : `재연결 시도 중 · ${secondsLeft}초 후`}
    </Box>
  );
}

// 흑건 하나를 골드로 칠한 미니 건반 — "조율 중" 표시
const WHITE_KEYS = 7;
const BLACK_KEY_OFFSETS = [1, 2, 4, 5, 6]; // 해당 백건의 왼쪽 경계에 걸치는 흑건
const GOLD_BLACK_KEY = 4;

function TuningKeyboard({ gold }) {
  const keyWidth = 20;
  return (
    <Box sx={{ position: 'relative', display: 'flex', width: keyWidth * WHITE_KEYS, height: 64, mx: 'auto' }}>
      {Array.from({ length: WHITE_KEYS }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: keyWidth,
            height: '100%',
            bgcolor: piano.ivory,
            border: `1px solid ${piano.keylineStrong}`,
            borderLeftWidth: i === 0 ? 1 : 0,
            borderRadius: '0 0 4px 4px',
          }}
        />
      ))}
      {BLACK_KEY_OFFSETS.map((offset) => (
        <Box
          key={offset}
          sx={{
            position: 'absolute',
            top: 0,
            left: offset * keyWidth - 6,
            width: 12,
            height: 40,
            bgcolor: offset === GOLD_BLACK_KEY ? gold : piano.ink,
            borderRadius: '0 0 3px 3px',
          }}
        />
      ))}
    </Box>
  );
}

export function MaintenanceScreen() {
  const theme = useTheme();
  const gold = theme.palette.mode === 'dark' ? piano.goldBright : piano.gold;
  const online = useIsOnline();
  // 세션 확인 단계부터 실패했으므로 복구 후에는 새로고침으로 앱을 처음부터 다시 띄운다
  // (배포 직후라면 새 번들도 함께 받는다)
  const { secondsLeft, checking, checkNow } = useRecoveryPolling(() => window.location.reload());

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        pt: 'env(safe-area-inset-top, 0px)',
        pb: 'env(safe-area-inset-bottom, 0px)',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 320, textAlign: 'center' }}>
        <TuningKeyboard gold={gold} />
        <Typography sx={{ mt: 3.5, mb: 1, fontSize: 20, fontWeight: 700 }}>
          {online ? '잠시 조율 중이에요' : '인터넷 연결이 끊겼어요'}
        </Typography>
        <Typography sx={{ mb: 3, fontSize: 14, color: 'text.secondary', lineHeight: 1.7 }}>
          {online ? '서비스 점검 중입니다.' : '네트워크 상태를 확인해 주세요.'}
          <br />
          곧 자동으로 다시 연결돼요.
        </Typography>
        <RetryStatus checking={checking} secondsLeft={secondsLeft} gold={gold} />
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={checkNow}
          disabled={checking}
          sx={{ mt: 3 }}
        >
          지금 다시 시도
        </Button>
      </Box>
    </Box>
  );
}

export function MaintenanceDialog() {
  const theme = useTheme();
  const gold = theme.palette.mode === 'dark' ? piano.goldBright : piano.gold;
  const online = useIsOnline();
  // 사용 중이던 화면(작성 중인 폼 등)을 보존하도록 새로고침 없이 모달만 닫는다
  const { secondsLeft, checking, checkNow } = useRecoveryPolling(markServerUp);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      // 페이지가 띄운 오류 다이얼로그보다 위에 표시
      sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
    >
      <DialogContent sx={{ textAlign: 'center', pt: 3.5, pb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.18)' : 'rgba(201, 162, 39, 0.14)',
            color: gold,
          }}
        >
          <RefreshRoundedIcon />
        </Box>
        <Typography sx={{ mb: 1, fontSize: 17, fontWeight: 700 }}>
          {online ? '서비스 점검 중' : '인터넷 연결 끊김'}
        </Typography>
        <Typography sx={{ mb: 2.5, fontSize: 14, color: 'text.secondary', lineHeight: 1.7 }}>
          연결되면 창이 자동으로 닫히고
          <br />
          이어서 쓸 수 있어요.
        </Typography>
        <RetryStatus checking={checking} secondsLeft={secondsLeft} gold={gold} />
        <Button
          variant="contained"
          fullWidth
          onClick={checkNow}
          disabled={checking}
          sx={{ mt: 2.5 }}
        >
          다시 시도
        </Button>
      </DialogContent>
    </Dialog>
  );
}
